import React, { useEffect, useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { Button, Field, Flex, Input, Message, Modal, Text } from '@workwhile/ui'
import { useIndeedFlexContextValue } from '../../context/IndeedFlexProvider'

export const linkIndeedFlexPositionFormSchema = z.object({
  companyPositionId: z.coerce
    .number({ invalid_type_error: 'Company position ID is required' })
    .int('Company position ID must be an integer')
    .positive('Company position ID must be positive'),
})

export type LinkIndeedFlexPositionForm = z.infer<
  typeof linkIndeedFlexPositionFormSchema
>

type LinkIndeedFlexPositionButtonProps = {
  ifClientId: number
  ifRoleId: number
  roleName: string
  clientName: string
}

export const LinkIndeedFlexPositionButton = ({
  ifClientId,
  ifRoleId,
  roleName,
  clientName,
}: LinkIndeedFlexPositionButtonProps) => {
  const {
    actions: { linkIndeedFlexPosition, resetLinkIndeedFlexPosition },
    state: { linkIndeedFlexPositionMutationState },
  } = useIndeedFlexContextValue()

  const [isOpen, setIsOpen] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<LinkIndeedFlexPositionForm>({
    resolver: zodResolver(linkIndeedFlexPositionFormSchema),
    defaultValues: {
      companyPositionId: undefined,
    },
  })

  useEffect(() => {
    if (!linkIndeedFlexPositionMutationState.isSuccess) {
      return
    }

    setIsOpen(false)
    reset()
    resetLinkIndeedFlexPosition()
  }, [
    linkIndeedFlexPositionMutationState.isSuccess,
    reset,
    resetLinkIndeedFlexPosition,
  ])

  const handleClick = () => {
    resetLinkIndeedFlexPosition()
    reset()
    setIsOpen(true)
  }

  const handleCancel = () => {
    resetLinkIndeedFlexPosition()
    reset()
    setIsOpen(false)
  }

  const onSubmit = handleSubmit((data) => {
    linkIndeedFlexPosition({
      ifClientId,
      ifRoleId,
      companyPositionId: data.companyPositionId,
    })
  })

  return (
    <>
      <Button
        variant="secondary"
        kind="small"
        onClick={handleClick}
        style={{ padding: '0 0.5rem' }}
        disabled={linkIndeedFlexPositionMutationState.isPending}
        loading={linkIndeedFlexPositionMutationState.isPending}
      >
        <Text fontSize="12px">Link position</Text>
      </Button>
      {isOpen ? (
        <Modal
          open
          onClose={handleCancel}
          title={`Link position for ${roleName}`}
        >
          <Text mb={2}>
            Enter the WorkWhile company position ID to link {roleName} (
            {ifRoleId}) for client {clientName} ({ifClientId}).
          </Text>
          {linkIndeedFlexPositionMutationState.isError &&
          linkIndeedFlexPositionMutationState.errorMessage ? (
            <Message variant="error" mb={3}>
              {linkIndeedFlexPositionMutationState.errorMessage}
            </Message>
          ) : null}
          <form onSubmit={onSubmit}>
            <Field
              label="Company position ID"
              error={errors.companyPositionId?.message}
            >
              <Input type="number" {...register('companyPositionId')} />
            </Field>
            <Flex flexDirection="row" justifyContent="space-between" gap={2}>
              <Button variant="secondary" onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={linkIndeedFlexPositionMutationState.isPending}
                disabled={linkIndeedFlexPositionMutationState.isPending}
              >
                Submit
              </Button>
            </Flex>
          </form>
        </Modal>
      ) : null}
    </>
  )
}
